/**
 * ticketStatus — presentation för ärendens status/prioritet/kategori/källa.
 *
 * Ärendena bor i den delade tickets-tabellen och kan komma från olika
 * appar (handel, åtkomst, gäst-flöden). Värdena speglar tabellens enums;
 * etiketter och badge-färger följer ärendeflödet i pwa-handel.
 */

export interface TicketStatusConfig {
  value: string;
  label: string;
  badgeClass: string;
}

const MUTED_BADGE = 'bg-muted text-muted-foreground border-border';

export const TICKET_STATUSES: TicketStatusConfig[] = [
  { value: 'new', label: 'Ny', badgeClass: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'open', label: 'Öppen', badgeClass: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { value: 'in_progress', label: 'Pågående', badgeClass: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { value: 'pending', label: 'Väntar', badgeClass: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'resolved', label: 'Löst', badgeClass: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'closed', label: 'Stängd', badgeClass: MUTED_BADGE },
  { value: 'cancelled', label: 'Avbruten', badgeClass: MUTED_BADGE },
];

/** Status-presentation med fallback för okända värden. */
export function ticketStatusConfig(status: string): TicketStatusConfig {
  return (
    TICKET_STATUSES.find((s) => s.value === status) ?? {
      value: status,
      label: status,
      badgeClass: MUTED_BADGE,
    }
  );
}

/** Statusar som admin kan filtrera på och sätta i ärendevyn. */
export const ADMIN_TICKET_STATUSES: TicketStatusConfig[] = TICKET_STATUSES.filter(
  (s) => !['pending', 'cancelled'].includes(s.value)
);

export const TICKET_PRIORITY_LABELS: Record<string, string> = {
  low: 'Låg',
  medium: 'Medel',
  high: 'Hög',
  urgent: 'Brådskande',
  emergency: 'Akut',
};

/** Prioriteter som lyfts fram visuellt i admin-listan. */
export const HIGH_PRIORITIES = ['high', 'urgent', 'emergency'];

export const TICKET_CATEGORY_LABELS: Record<string, string> = {
  order_question: 'Frågor om köp',
  product_question: 'Produktfråga',
  payment: 'Betalning',
  delivery: 'Leverans av varor',
  technical: 'Tekniskt problem',
  product_request: 'Produktönskemål',
  message: 'Meddelande',
  other: 'Övrigt',
  general: 'Allmänt',
  installation: 'Installation',
  maintenance: 'Underhåll',
  billing: 'Fakturering',
  access_issue: 'Åtkomstproblem',
};

/** Kategorietikett med fallback till råvärdet. */
export function ticketCategoryLabel(category: string): string {
  return TICKET_CATEGORY_LABELS[category] ?? category;
}

/**
 * Ursprungsapp/källa för ett ärende, ur metadata (ärenden kan komma från
 * olika appar). Returnerar null när ingen källa finns registrerad.
 */
export function ticketSourceLabel(metadata: Record<string, unknown> | null): string | null {
  const source = metadata?.source;
  return typeof source === 'string' && source.trim() ? source : null;
}
